/**
 * Rapportopbouw (P11, PLAN.md §11): celtekst, sectietoepasselijkheid en
 * dekkingsscore.
 *
 * Drie dingen horen hier en niet in de viewer:
 *
 * 1. **Celtekst.** Dezelfde waarde werd op drie plaatsen apart geformatteerd
 *    (viewer, CSV, PDF). Dat is niet alleen dubbel werk: het tier-C-label moet
 *    volgens PLAN.md §11 in de *tekst* van de cel staan, en een regel die in
 *    drie kopieën leeft is een regel die in twee ervan ontbreekt. Er is nu één
 *    functie, en de validator controleert de uitkomst ervan.
 * 2. **Sectietoepasselijkheid.** Een sectie zonder mogelijke bron wordt
 *    weggelaten, maar **zichtbaar**: een leeg schemaveld is het grootste
 *    hallucinatierisico in het systeem, en een stil weggelaten sectie laat de
 *    lezer denken dat er niets te melden was.
 * 3. **Dekkingsscore.** Hoeveel van de toepasselijke velden zijn gevuld — per
 *    subject, zodat zichtbaar is dat een concurrent met drie gevulde velden
 *    niet met het eigen bedrijf te vergelijken is.
 */
import { PROVENANCE_TIER_CELL_PREFIX, AVAILABILITY_LABELS, METRIC_CATEGORY_LABELS, } from './report-labels.js';
import { METRIC_CATALOG, RETIRED_METRICS, connectorCodeForFamily, hasRunnableWriter, isDefinitionAllowedForSubject, } from './metrics.js';
// ─── Celtekst ────────────────────────────────────────────────────────────────
const NUMBER_FORMAT = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 2 });
/**
 * Vormt de weergegeven tekst van één cel, inclusief het tier-C-voorvoegsel.
 *
 * Ontbrekende gegevens leveren het beschikbaarheidslabel ("Niet beschikbaar",
 * "Niet opgevraagd", …) en nooit `0` of een lege string: het verschil tussen
 * "gevraagd, niets gevonden" en "niet gevraagd" is precies wat een lezer nodig
 * heeft om te weten of hij de bron moet aanzetten of moet vergeten.
 */
export function formatCellText(cell, options = {}) {
    const { listSeparator = ', ', emptyListText = 'Geen', missingText } = options;
    if (cell.availability !== 'AVAILABLE' || !cell.value) {
        return missingText ?? AVAILABILITY_LABELS[cell.availability];
    }
    const body = formatValue(cell.value, { listSeparator, emptyListText });
    if (body === null)
        return missingText ?? AVAILABILITY_LABELS.NOT_AVAILABLE;
    return `${cellTextPrefix(cell)}${body}`;
}
/**
 * Voorvoegsel van de celtekst. Een AI-gegenereerde waarde draagt haar label in
 * de tekst zelf, omdat rapporten naar decks en e-mails worden gekopieerd: daar
 * overleeft woordkeus wel en opmaak niet. Een handmatige correctie krijgt geen
 * voorvoegsel — die is per definitie niet door AI gemaakt en wordt apart
 * gelabeld.
 */
export function cellTextPrefix(cell) {
    if (cell.correction)
        return '';
    if (!cell.provenanceTier)
        return '';
    return PROVENANCE_TIER_CELL_PREFIX[cell.provenanceTier] ?? '';
}
function formatValue(v, options) {
    switch (v.valueType) {
        case 'NUMBER':
            return v.number === null ? null : NUMBER_FORMAT.format(v.number);
        case 'BOOLEAN':
            return v.boolean === null ? null : v.boolean ? 'Ja' : 'Nee';
        case 'TEXT':
            return v.text === null || v.text === '' ? null : v.text;
        case 'LIST':
            return v.list.length > 0 ? v.list.join(options.listSeparator) : options.emptyListText;
        case 'RANGE':
            return v.low === null || v.high === null
                ? null
                : `${NUMBER_FORMAT.format(v.low)} – ${NUMBER_FORMAT.format(v.high)}`;
    }
}
/**
 * Volledigheid van één rij. `NOT_APPLICABLE` telt niet mee als gat: die cel
 * hoort er niet te zijn en zou de rij anders onterecht incompleet maken.
 */
export function rowCompleteness(row) {
    const applicableCells = row.cells.filter((c) => c.availability !== 'NOT_APPLICABLE');
    const known = applicableCells.filter((c) => c.availability === 'AVAILABLE').length;
    const applicable = applicableCells.length;
    const complete = applicable > 0 && known === applicable;
    return {
        applicable,
        known,
        labelNl: complete ? null : `${known} van ${applicable} bekend`,
        rankable: complete,
    };
}
/**
 * Dekking per subject: hoeveel van de toepasselijke velden zijn gevuld.
 *
 * "Toepasselijk" sluit twee dingen uit: een `companyOnly`-metriek bij een
 * concurrent (die mág daar niet staan) en een cel die `NOT_APPLICABLE` is. Zo
 * wordt een concurrent niet afgerekend op velden die per definitie niet over
 * hem kunnen gaan.
 */
export function computeCoverage(subjects, rows) {
    return subjects.map((subject) => {
        let applicableFields = 0;
        let filledFields = 0;
        for (const row of rows) {
            const def = metricDefinition(row.metricCode);
            if (def && !isDefinitionAllowedForSubject(def, subject.subjectType))
                continue;
            const cell = row.cells.find((c) => c.subjectKey === subject.key);
            if (cell && cell.availability === 'NOT_APPLICABLE')
                continue;
            applicableFields += 1;
            if (cell?.availability === 'AVAILABLE')
                filledFields += 1;
        }
        const ratio = applicableFields === 0 ? null : filledFields / applicableFields;
        return {
            subjectKey: subject.key,
            applicableFields,
            filledFields,
            ratio,
            labelNl: ratio === null
                ? 'Geen toepasselijke velden'
                : `${filledFields} van ${applicableFields} velden gevuld (${Math.round(ratio * 100)}%)`,
        };
    });
}
const DEFINITIONS_BY_CODE = new Map([...METRIC_CATALOG, ...RETIRED_METRICS].map((m) => [m.code, m]));
function metricDefinition(code) {
    return DEFINITIONS_BY_CODE.get(code);
}
// ─── Sectietoepasselijkheid ──────────────────────────────────────────────────
export const SECTION_OMISSION_REASONS = [
    /**
     * Elke metriek van deze sectie staat in `RETIRED_METRICS`: er is in deze
     * bronnenset geen levende bron meer voor. Dit is de enige reden waarom een
     * sectie *structureel* niet gevuld kan worden.
     */
    'NO_LIVING_SOURCE',
    /**
     * Elke bron van deze sectie is een connector die uit staat (P18). Wezenlijk
     * anders dan `NO_DATA_COLLECTED`: daar is gezocht en niets gevonden, hier is
     * niets geprobeerd. Dat verschil bepaalt of de lezer een bron moet aanzetten
     * of moet accepteren dat er niets is.
     */
    'CONNECTOR_DISABLED',
    /** De probe (P6) sloot voor élke metriek van de sectie elke bron uit. */
    'SOURCE_UNAVAILABLE',
    /** Levende bronnen, maar deze run leverde geen enkele observatie op. */
    'NO_DATA_COLLECTED',
    /** Een SBI-regel sluit de sectie voor deze branche uit. */
    'NOT_APPLICABLE_FOR_BRANCHE',
];
/**
 * SBI-afhankelijke uitsluitingen, per categorie de sectiecodes (A–U) waarvoor
 * de sectie niet van toepassing is.
 *
 * **Bewust leeg.** PLAN.md §11 vraagt branche-conditionele secties, maar een
 * regel als "SEA is niet van toepassing voor metaalbewerking" heb ik nergens
 * op kunnen baseren. Zo'n verzonnen regel is erger dan geen regel: hij zet een
 * echt gat ("we hebben niets gevonden") om in een gerust makende bewering
 * ("hier valt niets te vinden"), en dat is precies de stille fout waar dit
 * plan vanaf wil. Het mechanisme staat er en is getest; de regels zelf hebben
 * een onderbouwde bron of een vakinhoudelijk besluit nodig. Zie PLAN.md §14.
 */
export const SBI_SECTION_EXCLUSIONS = {};
const LIVE_CODES_BY_CATEGORY = groupCodes(METRIC_CATALOG);
const ALL_CODES_BY_CATEGORY = groupCodes([...METRIC_CATALOG, ...RETIRED_METRICS]);
function groupCodes(definitions) {
    const map = new Map();
    for (const def of definitions) {
        map.set(def.category, [...(map.get(def.category) ?? []), def.code]);
    }
    return map;
}
/** Vaste weergavevolgorde: eerst wat over het bedrijf zelf gaat, dan de kanalen. */
export const SECTION_ORDER = [
    'POSITIONING',
    'WEBSITE',
    'SEO',
    'SEA',
    'SEARCH_CONSOLE',
    'FIRST_PARTY',
    'SOCIAL',
    'REPUTATION',
];
/**
 * Bepaalt per sectie of hij in het rapport komt, en zo niet: waarom niet.
 *
 * Een sectie mét rijen doet altijd mee — data die er is wordt nooit verborgen,
 * ook niet als een regel zegt dat de sectie niet van toepassing zou zijn. Zo'n
 * combinatie is een signaal dat de regel fout is, en die wil je zien in plaats
 * van wegpoetsen.
 */
export function evaluateReportSections(input) {
    const { rowCountByCategory, sbiSection = null, hasNoPossibleSource, sbiExclusions = SBI_SECTION_EXCLUSIONS, runnableConnectors, disabledReasons, } = input;
    return SECTION_ORDER.map((category) => {
        const labelNl = METRIC_CATEGORY_LABELS[category];
        const rowCount = rowCountByCategory.get(category) ?? 0;
        if (rowCount > 0) {
            return { category, labelNl, included: true, omissionReason: null, messageNl: null, rowCount };
        }
        const omission = omissionFor(category, sbiSection, hasNoPossibleSource, sbiExclusions, {
            runnableConnectors,
            disabledReasons,
        });
        return {
            category,
            labelNl,
            included: false,
            omissionReason: omission.reason,
            messageNl: omission.messageNl,
            rowCount: 0,
        };
    });
}
function omissionFor(category, sbiSection, hasNoPossibleSource, sbiExclusions, connectors) {
    const liveCodes = LIVE_CODES_BY_CATEGORY.get(category) ?? [];
    if (liveCodes.length === 0) {
        const retiredCount = (ALL_CODES_BY_CATEGORY.get(category) ?? []).length;
        return {
            reason: 'NO_LIVING_SOURCE',
            messageNl: retiredCount > 0
                ? 'Niet opgenomen: er is in de huidige bronnenset geen bron meer voor deze sectie.'
                : 'Niet opgenomen: voor deze sectie zijn geen metrieken gedefinieerd.',
        };
    }
    const excluded = sbiExclusions[category] ?? [];
    if (sbiSection && excluded.includes(sbiSection)) {
        return {
            reason: 'NOT_APPLICABLE_FOR_BRANCHE',
            messageNl: `Niet van toepassing voor deze branche (SBI-sectie ${sbiSection}).`,
        };
    }
    // Staat élke bron van deze sectie uit, dan is dat de reden — en niet "geen
    // waarden gevonden", want er is niets geprobeerd.
    const runnable = connectors.runnableConnectors;
    if (runnable) {
        const definities = liveCodes
            .map((code) => DEFINITIONS_BY_CODE.get(code))
            .filter((d) => d !== undefined);
        if (definities.length > 0 && definities.every((d) => !hasRunnableWriter(d, runnable))) {
            const codes = [
                ...new Set(definities.flatMap((d) => d.sourcePreference
                    .map((f) => connectorCodeForFamily(f))
                    .filter((c) => c !== null))),
            ];
            const redenen = codes
                .map((code) => connectors.disabledReasons?.get(code))
                .filter((r) => Boolean(r));
            return {
                reason: 'CONNECTOR_DISABLED',
                messageNl: `Niet opgenomen: de bron${codes.length === 1 ? '' : 'nen'} voor deze sectie ` +
                    `(${codes.join(', ')}) ${codes.length === 1 ? 'stond' : 'stonden'} uit tijdens deze uitvoering, ` +
                    `dus er is niets opgevraagd.` +
                    (redenen.length > 0 ? ` Reden: ${redenen.join(' ')}` : ''),
            };
        }
    }
    if (hasNoPossibleSource && liveCodes.every((code) => hasNoPossibleSource(code))) {
        return {
            reason: 'SOURCE_UNAVAILABLE',
            messageNl: 'Niet opgenomen: de beschikbaarheidscontrole vond voor geen enkel veld van deze sectie een bron die kan leveren.',
        };
    }
    return {
        reason: 'NO_DATA_COLLECTED',
        messageNl: 'Niet opgenomen: deze uitvoering leverde geen waarden voor deze sectie op.',
    };
}
//# sourceMappingURL=report.js.map